/**
 * Approvals Module - Type Definitions
 * Handles enrollment approval workflows with support for individual and bulk approval types
 */

/**
 * User information for approval
 */
export interface ApprovalUserInfo {
  user_id: number;
  user_role_id: number;
  enrollment_status: number;
  enrollment_status_text: string;
  created_date: string;
  city: string;
  region: string;
  email: string;
  full_name: string;
  aadhar_number: string;
  mobile: string;
  address: string;
  outlet_name: string;
  dob: string;
  pincode: string;
  pan_image: string;
  'Bank image': string;
  state: string;
  profile_image: string;
  outlet_code: string;
  aadhar_image: string;
  pan_number: string;
  gst_number: string;
}

/**
 * Approval Types
 * 1 = Individual (Field-level approval)
 * Other = Bulk approval (approve/reject entire submission)
 */
export type ApprovalType = 1 | number;

/**
 * Enrollment setting configuration
 */
export interface EnrollmentSetting {
  enrollment_setting_id: number;
  setting_name: string;
  approval_type: ApprovalType;
  approval_type_text: string;
  approval_required: boolean;
  login_before_approval: boolean;
  approval_pending_message: string;
  approval_rejection_message: string;
}

/**
 * Workflow tier information
 */
export interface WorkflowTier {
  workflow_id: number;
  tier: number;
  role_id: number;
  role_name: string | null;
  require_all_approvals: boolean;
  auto_approval: 0 | 1;
  is_approved: boolean;
  is_current_tier: boolean;
}

/**
 * Approval workflow structure
 */
export interface ApprovalWorkflow {
  current_pending_tier: number;
  total_tiers: number;
  tiers: WorkflowTier[];
}

/**
 * Field data types
 */
export type FieldDataType = 'text' | 'email' | 'phone' | 'date' | 'dropdown' | 'file';

/**
 * Approval field - Fields that require approval decision
 * (Only for approval_type === 1)
 */
export interface ApprovalField {
  enrollment_setting_field_id: number;
  field_master_id: number;
  field_code: string;
  field_name: string;
  field_label: string;
  data_type: FieldDataType;
  display_order: number;
  field_value: string | null;
  verification_data: any | null;
  approval_field_id: number;
  field_approval_status?: FieldApprovalStatus;
}

/**
 * Information field - Read-only fields for reference
 */
export interface InformationField {
  enrollment_setting_field_id: number;
  field_master_id: number;
  field_code: string;
  field_name: string;
  field_label: string;
  data_type: FieldDataType;
  display_order: number;
  field_value: string | null;
  verification_data: any | null;
}

/**
 * Approval history entry
 */
export interface ApprovalHistoryEntry {
  approval_id: number;
  user_id: number;
  approver_id: number;
  approver_name: string;
  tier: number;
  action: 1 | 2; // 1=approved, 2=rejected
  action_text: string;
  comments: string;
  rejection_reason: string | null;
  created_date: string;
}

/**
 * Approver permissions
 */
export interface ApproverPermissions {
  can_approve: boolean;
  view_only?: boolean;
  approver_tier: number | null;
  auto_approval: 0 | 1 | null;
  can_approve_current_tier: boolean;
  can_review_kyc?: boolean;
}

/**
 * Active approval mode from getuserdetails
 * "kyc" = KYC verification review flow
 */
export type ActiveApprovalType = 'kyc' | string | null;

/**
 * KYC verification item status codes
 * 1 = Pending, 2 = Approved, 3 = Rejected
 */
export type KycAgentStatusCode = 1 | 2 | 3;

/**
 * Single KYC verification item from getuserdetails
 */
export interface KycVerificationItem {
  verification: string; // pan | aadhaar | gst | bank
  label: string;
  agent_status: string; // pending | approved | rejected
  agent_status_code: KycAgentStatusCode;
  can_review: boolean;
  can_submit?: boolean;
  user_status?: string;
  user_status_code?: number;
  values?: Record<string, string | null | undefined>;
  verification_data?: any | null;
  /** Present when agent_status is rejected */
  rejection_reason?: string | null;
}

/**
 * Local UI state for a KYC verification decision before submit
 */
export interface KycVerificationState {
  verification: string;
  label: string;
  agent_status: string;
  agent_status_code: KycAgentStatusCode;
  can_review: boolean;
  /** Local decision: 0 = undecided, 1 = approve, 2 = reject */
  action: 0 | 1 | 2;
  rejection_reason: string;
}

/**
 * User approval details (the actual data structure)
 */
export interface UserApprovalDetails {
  user_info: ApprovalUserInfo;
  enrollment_setting: EnrollmentSetting;
  approval_workflow: ApprovalWorkflow;
  approval_fields: ApprovalField[];
  information_fields: InformationField[];
  approval_history: ApprovalHistoryEntry[];
  approver_permissions: ApproverPermissions;
  /** Present when this user is in an active approval mode (e.g. "kyc") */
  active_approval?: ActiveApprovalType;
  /** KYC verification list — present when active_approval === "kyc" */
  kyc_verification?: KycVerificationItem[];
  latest_rejection?: {
    rejection_reason?: string | null;
    comments?: string | null;
    [key: string]: any;
  } | null;
  rejection_history?: Array<{
    rejection_reason?: string | null;
    comments?: string | null;
    [key: string]: any;
  }>;
}

/**
 * Complete user details API response for approval
 * API returns data wrapped in a 'data' property
 */
export interface UserApprovalDetailsResponse {
  data: UserApprovalDetails;
}

// ============= ACTION PAYLOAD TYPES =============

/**
 * Approval status for individual fields
 * 0 = Pending
 * 1 = Approved
 * 2 = Rejected
 */
export type FieldApprovalStatus = 0 | 1 | 2;

/**
 * Field approval decision
 * (Required when approval_type === 1)
 */
export interface FieldApprovalDecision {
  field_master_id: number;
  approval_status: FieldApprovalStatus;
  comments?: string;
}

/**
 * Action type
 * 1 = Approve
 * 2 = Reject
 */
export type ApprovalAction = 1 | 2;

/**
 * Approval action payload for approval_type === 1 (Individual approval)
 */
export interface IndividualApprovalPayload {
  target_user_id: number;
  action: ApprovalAction;
  comments?: string;
  rejection_reason?: string; // Required if action === 2
  field_approvals: FieldApprovalDecision[];
}

/**
 * Approval action payload for approval_type !== 1 (Bulk approval)
 */
export interface BulkApprovalPayload {
  target_user_id: number;
  action: ApprovalAction;
  comments?: string;
  rejection_reason?: string; // Required if action === 2
}

/**
 * Single verification decision for KYC submit
 */
export interface VerificationActionItem {
  verification: string;
  action: ApprovalAction;
  rejection_reason?: string;
}

/**
 * Payload for POST enrollment-approval/verification-action
 */
export interface VerificationActionPayload {
  target_user_id: number;
  verifications: VerificationActionItem[];
}

/**
 * Union type for approval payloads
 */
export type ApprovalActionPayload = IndividualApprovalPayload | BulkApprovalPayload;

// ============= HELPER TYPES =============

/**
 * Type guard to check if approval is individual type
 */
export function isIndividualApproval(
  enrollmentSetting: EnrollmentSetting
): boolean {
  return enrollmentSetting.approval_type === 1;
}

/**
 * Type guard to check if payload is individual approval
 */
export function isIndividualApprovalPayload(
  payload: ApprovalActionPayload
): payload is IndividualApprovalPayload {
  return 'field_approvals' in payload;
}

/**
 * Field approval state for UI management
 * Used to track approval decisions before submission
 */
export interface FieldApprovalState {
  field_master_id: number;
  field_code: string;
  field_name: string;
  field_value: string | null;
  approval_status: FieldApprovalStatus;
  comments: string;
  data_type: FieldDataType;
}

/**
 * UI state for approval management
 */
export interface ApprovalUIState {
  isIndividualApproval: boolean;
  fieldStates: Map<number, FieldApprovalState>; // key: field_master_id
  globalAction: ApprovalAction | null;
  globalComments: string;
  rejectionReason: string;
}
